import { getReceivedContracts, hasReceivedPsaContract, isUnsignableNafContract } from "./receivedContracts";

// One definition of "this provider org is past its required-documents deadline".
// Shared by the DME sign entry points (isSignLocked), the Layout reminder and
// the prompt, so the sign lock, the reminder and the legacy-reminder
// suppression can never disagree about the same org.
//
// The lock gates the SIGN action only (revision 2026-10-08): a locked org can
// still open /orders and /peptides, create, edit and submit DME orders for
// signature. Signing is refused here and by the backend Submit Order gate;
// peptide checkout is refused by the backend only.
//
// Deliberately dependency-free apart from ./receivedContracts: api.js /
// utils.js / contractApi.js / templateApi.js pull in ESM-only transitive deps
// (`marked`) that Jest can't transform, which would make this module — and its
// unit test — unloadable. See the header comment in receivedContracts.js.
//
// Deadlines are stamped SERVER-side (`orgInfo.requiredDocsDueAt`,
// `customContracts[i].signDueAt`). "Locked" is never stored; it is derived here
// as "unsigned AND past due". A MISSING stamp therefore means grandfathered and
// can never lock — that is the whole rollout strategy, so every read of a due
// date below fails open.
//
// Contract: stamps MUST carry a zone designator. A zone-less ISO date-time
// (e.g. "2026-09-28T11:00:00", no trailing Z/z/±hh:mm) is read as UTC, not
// browser-local time — otherwise the same stamp locks at a different moment
// depending on the viewer's timezone. Date-only values ("2026-09-28") are
// already UTC per ECMA-262 and are left untouched. Accepted shapes: an ISO
// date, or an ISO date-time with optional seconds/fraction and an optional
// zone designator (Z/z or ±hh:mm/±hhmm). Anything else — space-separated,
// slash-separated, textual, or otherwise unrecognised — returns null (fails
// open) rather than being guessed at by the engine's legacy fallback parser.

const PROVIDER_PLAN = "order-provider";
const CONTRACT_STATUS_SENT = "sent";
const CONTRACT_STATUS_SIGNED = "signed";
const DAY_MS = 86400000;

export const SIGN_PATHS = {
  contract: "/settings/receivedContracts",
  naf: "/settings/newAccountForm",
  psa: "/settings/productServiceAgreement",
};

// Backend block code carried by the `{ blocked: true, errorCode }` order-create
// idiom (see utils/peptideCheckoutSubmission.js) when an order is refused
// because the org's required documents are overdue.
export const DOCUMENTS_OVERDUE = "documents_overdue";

// Event name the DME api wrappers and the peptide checkout publish so the
// locked prompt + orgInfo refresh live in exactly ONE place
// (RequiredDocsLockHost) instead of at each call site.
export const DOCUMENTS_OVERDUE_EVENT = "documentsOverdue";

// Published by a DME sign entry point that refused to sign because the org is
// locked; RequiredDocsLockHost answers it with the locked prompt.
export const SIGN_BLOCKED_EVENT = "requiredDocsSignBlocked";

export function isDocumentsOverdueOutput(output) {
  return !!output && output.blocked === true && output.errorCode === DOCUMENTS_OVERDUE;
}

export function makeDocumentsOverdueError(output) {
  const err = new Error(DOCUMENTS_OVERDUE);
  err.code = DOCUMENTS_OVERDUE;
  err.blockedMessage = (output && output.errorMessage) || null;
  return err;
}

export function isDocumentsOverdueError(err) {
  return !!err && err.code === DOCUMENTS_OVERDUE;
}

// Accepted stamp shapes: an ISO date ("2026-09-28") or an ISO date-time with an
// optional zone designator. ANY other shape returns null (fail open) —
// notably the space-separated SQL/JDBC form "2026-09-28 11:00:00",
// "2026/09/28 11:00:00" and "Sep 28 2026": BOTH V8 and JavaScriptCore read
// those through their legacy fallback parsers as browser-local time, so the
// FE would lock at a different moment for every viewer timezone — in every
// major browser. The server is authoritative; never lock on a stamp this
// module cannot read unambiguously.
const ACCEPTED_DUE_AT = /^\d{4}-\d{2}-\d{2}([Tt]\d{2}:\d{2}(:\d{2}(\.\d+)?)?([Zz]|[+-]\d{2}:?\d{2})?)?$/;

// Zone-less by construction — no trailing Z/z/±hh:mm. Read it as UTC so the
// same stamp locks at the same instant everywhere. Date-only values don't
// match and are already UTC per ECMA-262.
const ZONELESS_DATETIME = /^\d{4}-\d{2}-\d{2}[Tt]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

// Absent, non-string, unrecognised or unparseable → null. Never lock on data
// we can't read. Returns the ZONE-CORRECTED string (not just a parsed
// instant) because makeItem/compareItems/nextDueAt/daysLeft all re-parse
// this return value.
function normalizeDueAt(value) {
  if (typeof value !== "string" || value === "") return null;
  if (!ACCEPTED_DUE_AT.test(value)) return null;
  const zoned = ZONELESS_DATETIME.test(value) ? `${value}Z` : value;
  return Number.isNaN(Date.parse(zoned)) ? null : zoned;
}

function isObjectRecord(value) {
  return value != null && typeof value === "object";
}

// `orgInfo.requiredDocsExternalSignoffs` is a manufacturer supervisor's
// attestation that a document was signed outside the system. Field-for-field
// mirror of hasActiveExternalSignoff in lucasai-backend
// backend/lib/requiredDocsLock.js — the BE sign/checkout gates and this module
// must agree on every shape. Only `active === true` grants the bypass: a
// malformed record is not an unreadable deadline, so it gets no fail-open
// benefit. `kind` is "naf" | "psa" | "contract"; `key` is the contractId.
export function hasActiveExternalSignoff(orgInfo, kind, key) {
  const signoffs = orgInfo ? orgInfo.requiredDocsExternalSignoffs : null;
  if (!isObjectRecord(signoffs)) return false;
  let entry = null;
  if (kind === "naf" || kind === "psa") {
    entry = signoffs[kind];
  } else if (kind === "contract") {
    const contracts = signoffs.contracts;
    if (!isObjectRecord(contracts)) return false;
    if (typeof key !== "string" || key === "") return false;
    if (!Object.prototype.hasOwnProperty.call(contracts, key)) return false;
    entry = contracts[key];
  }
  return isObjectRecord(entry) && entry.active === true;
}

function makeItem({ key, kind, title, titleKey, dueAt, signed, signedExternally }, now) {
  const normalizedDueAt = normalizeDueAt(dueAt);
  return {
    key,
    kind,
    ...(title ? { title } : {}),
    ...(titleKey ? { titleKey } : {}),
    dueAt: normalizedDueAt,
    signed,
    ...(signedExternally ? { signedExternally: true } : {}),
    overdue: !signed && normalizedDueAt !== null && Date.parse(normalizedDueAt) <= now,
    signPath: SIGN_PATHS[kind],
  };
}

// A real signature wins the label: signedExternally is set only when the
// signoff is what made the item count as signed.
function signedState(reallySigned, externallySigned) {
  return { signed: reallySigned || externallySigned, signedExternally: !reallySigned && externallySigned };
}

// Overdue first, then unsigned by nearest deadline (undated last), then signed.
// The first element is what "Sign now" targets.
function compareItems(a, b) {
  if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
  if (a.signed !== b.signed) return a.signed ? 1 : -1;
  if (a.dueAt === b.dueAt) return 0;
  if (a.dueAt === null) return 1;
  if (b.dueAt === null) return -1;
  return Date.parse(a.dueAt) - Date.parse(b.dueAt);
}

const EXEMPT = { applies: false, hasDeadline: false, locked: false, items: [], nextDueAt: null, daysLeft: null };

export function getRequiredDocsLock(auth, { now = Date.now(), isAdminUser = false } = {}) {
  const orgInfo = auth?.orgInfo;
  const applies =
    auth?.session?.currentOrganizationPlan === PROVIDER_PLAN &&
    !!orgInfo &&
    auth?.user?.role !== "administrator" &&
    !isAdminUser &&
    !auth?.offline;

  if (!applies) return { ...EXEMPT };

  const items = [];

  // Manufacturer-pushed contracts. Rows in any other status (draft, revoked)
  // aren't the provider's to sign. Unsignable naf-schema rows are excluded
  // entirely — never lock someone out over a document they cannot sign.
  for (const contract of getReceivedContracts(orgInfo)) {
    const status = contract.status;
    if (status !== CONTRACT_STATUS_SENT && status !== CONTRACT_STATUS_SIGNED) continue;
    if (isUnsignableNafContract(contract)) continue;
    items.push(
      makeItem(
        {
          key: contract.contractId,
          kind: "contract",
          title: contract.title,
          dueAt: contract.signDueAt,
          ...signedState(status === CONTRACT_STATUS_SIGNED, hasActiveExternalSignoff(orgInfo, "contract", contract.contractId)),
        },
        now
      )
    );
  }

  const dmeEnabled = orgInfo.productLines?.dme === true;

  // The New Account Form is DME credentialing paperwork — peptides-only orgs
  // are never asked for it (matches the legacy Layout reminder's gate).
  if (dmeEnabled) {
    items.push(
      makeItem(
        {
          key: "naf",
          kind: "naf",
          titleKey: "common.requiredDocsLock.docs.naf",
          dueAt: orgInfo.requiredDocsDueAt,
          ...signedState(!!orgInfo.newAccountForm?.completedAt, hasActiveExternalSignoff(orgInfo, "naf")),
        },
        now
      )
    );
  }

  // Standalone PSA only when the manufacturer required one AND did not push it
  // as a contract — when it was pushed, the contract row above is the real
  // signing surface and counting both would double-lock on one document.
  if (orgInfo.requirePsa === true && dmeEnabled && !hasReceivedPsaContract(orgInfo)) {
    items.push(
      makeItem(
        {
          key: "psa",
          kind: "psa",
          titleKey: "common.requiredDocsLock.docs.psa",
          dueAt: orgInfo.requiredDocsDueAt,
          ...signedState(!!orgInfo.productServiceAgreement?.signedAt, hasActiveExternalSignoff(orgInfo, "psa")),
        },
        now
      )
    );
  }

  items.sort(compareItems);

  const deadlineBearing = items.filter((i) => !i.signed && i.dueAt !== null);
  const nextDueAt = deadlineBearing.reduce((earliest, item) => {
    if (earliest === null) return item.dueAt;
    return Date.parse(item.dueAt) < Date.parse(earliest) ? item.dueAt : earliest;
  }, null);

  return {
    applies: true,
    // False once every deadline-bearing item is signed, so an org left with
    // only grandfathered (undated) unsigned documents falls back to the legacy
    // reminders instead of being silently un-nagged.
    hasDeadline: deadlineBearing.length > 0,
    locked: items.some((i) => i.overdue),
    items,
    nextDueAt,
    daysLeft: nextDueAt === null ? null : Math.max(0, Math.ceil((Date.parse(nextDueAt) - now) / DAY_MS)),
  };
}

export function isSignLocked(auth, opts) {
  return getRequiredDocsLock(auth, opts).locked;
}

export function firstSignPath(lock) {
  const first = (lock?.items || []).find((i) => !i.signed);
  return first ? first.signPath : null;
}
